import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ArrowRightOutlined,
    FlagFilled,
    FlagOutlined,
} from '@ant-design/icons';
import cn from 'classnames';

import { roundToDecimals } from '@/shared/lib/math';

import cls from './value-formatter.module.css';

export function ColorValue({ color }: { color: string }): JSX.Element {
    const baseColor = color || '#FFFFFF';

    return (
        <div className={cls.colorContainer}>
            <div
                className={cls.colorColumn}
                style={{ backgroundColor: baseColor }}
            >
                <div className={cls.colorOverlay} />
            </div>
        </div>
    );
}

export function FlagValue({
    isChecked,
    onFlagChange,
    id,
    color,
}: {
    isChecked: boolean;
    onFlagChange: (id: number, checked: boolean) => void;
    id: number;
    color?: string;
}): JSX.Element {
    const handleClick = () => {
        onFlagChange(id, !isChecked);
    };

    return (
        <div
            className={cn(cls.flagColumn, {
                [cls.activeFlag]: isChecked,
            })}
            onClick={handleClick}
        >
            {isChecked && (
                <div
                    className={cls.greenBackground}
                    style={
                        color ? { backgroundColor: `${color}10` } : undefined
                    }
                />
            )}
            {isChecked ? (
                <FlagFilled
                    className={cn(cls.flagIcon, cls.flagChecked)}
                    style={color ? { color } : undefined}
                />
            ) : (
                <FlagOutlined className={cn(cls.flagIcon, cls.flagUnchecked)} />
            )}
        </div>
    );
}

export interface GradientBadgeProps {
    icon?: ReactElement;
    label: string | number;
    variant: 'green' | 'red' | 'blue';
}

export function GradientBadge({
    icon,
    label,
    variant,
}: GradientBadgeProps): JSX.Element {
    return (
        <div className={cn(cls.gradientBadge, cls[variant])}>
            {icon && <span className={cls.badgeIcon}>{icon}</span>}
            <span>{label}</span>
        </div>
    );
}

export function PercentageValue({
    value,
    options: {
        precision = 2,
        shouldShowDirection = true,
        isAbsoluteValue = false,
        shouldShowSign = true,
        shouldUseColorsBasedOnSign = true,
    } = {},
}: {
    value: number;
    options?: {
        precision?: number;
        shouldShowDirection?: boolean;
        isAbsoluteValue?: boolean;
        shouldShowSign?: boolean;
        shouldUseColorsBasedOnSign?: boolean;
    };
}): JSX.Element {
    const roundedValue = roundToDecimals(value, precision);
    const displayValue = Math.abs(roundedValue).toFixed(precision);

    let variant: 'green' | 'red' | 'blue' = 'blue';
    let sign = '';

    if (shouldUseColorsBasedOnSign && roundedValue > 0) {
        variant = 'green';
    } else if (shouldUseColorsBasedOnSign && roundedValue < 0) {
        variant = 'red';
    }

    if (!isAbsoluteValue && shouldShowSign && roundedValue > 0) {
        sign = '+';
    } else if (!isAbsoluteValue && shouldShowSign && roundedValue < 0) {
        sign = '-';
    }

    let arrowRotation = 0;

    if (shouldShowDirection && roundedValue > 0) {
        arrowRotation = -45;
    } else if (shouldShowDirection && roundedValue < 0) {
        arrowRotation = 45;
    }

    const label = `${sign}${displayValue}%`;

    const arrow = (
        <ArrowRightOutlined
            className={cls.arrow}
            style={{ transform: `rotate(${arrowRotation}deg)` }}
        />
    );

    return (
        <GradientBadge
            label={label}
            variant={variant}
            {...(shouldShowDirection && { icon: arrow })}
        />
    );
}

export function DirectionTypeValue({
    isRecovered,
}: {
    isRecovered: boolean;
}): JSX.Element {
    const { t } = useTranslation();
    const displayText = !isRecovered
        ? t('table:columns.type_recovered')
        : t('table:columns.type_ongoing');

    return (
        <div
            className={cn(cls.directionType, {
                [cls.recovered]: !isRecovered,
                [cls.ongoing]: isRecovered,
            })}
        >
            <span>{displayText}</span>
        </div>
    );
}
